import { useEffect, useRef, useState } from 'react'
import { Terminal as XTerm } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { io } from 'socket.io-client'
import '@xterm/xterm/css/xterm.css'
import { apiUrl, authFetch } from '../../lib/api'
import { addNotification } from '../Notifications/NotificationBell'

const WS_URL = ((import.meta as any).env?.VITE_WS_URL ?? 'ws://localhost:3001').replace(/^ws/, 'http')

function getToken(): string {
  try { return JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? '' }
  catch { return '' }
}

const FILE_CHANGE_PATTERNS = [
  /created|scaffolding|installing|added \d+ packages|npm warn|success/i,
  /Done in|Wrote|Generated/i,
]

const ERROR_PATTERNS = [
  /Error:|error:|ERR!|FAILED|SyntaxError|TypeError|ReferenceError|Cannot find module|ENOENT|command not found/i,
  /WebException|WebCmdlet|InvalidOperation|Cannot GET|Cannot POST|Cannot PUT|Cannot DELETE/i,
  /StatusCode\s*:\s*[45]\d\d/i,
  /Bad Request|Not Found|Internal Server Error|Unauthorized|Forbidden/i,
]

export function TerminalPanel() {
  const containerRef = useRef<HTMLDivElement>(null)
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>()
  const [lastError, setLastError] = useState<string | null>(null)
  const [fixing, setFixing] = useState(false)
  const errorBuf = useRef('')

  useEffect(() => {
    if (!containerRef.current) return

    const term = new XTerm({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      theme: {
        background: '#0c0e18',
        foreground: '#eeeef5',
        cursor: '#7c6ef5',
        selectionBackground: 'rgba(124,110,245,0.3)',
        black: '#0c0e18',
        red: '#f07070',
        green: '#5fd4a4',
        yellow: '#f5a157',
        blue: '#60a5fa',
        magenta: '#7c6ef5',
        cyan: '#61dafb',
        white: '#eeeef5',
      },
    })

    const fitAddon = new FitAddon()
    term.loadAddon(fitAddon)
    term.open(containerRef.current)
    fitAddon.fit()

    const token = getToken()
    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
      query: { token },
    })

    socket.on('connect', () => {
      const dims = fitAddon.proposeDimensions()
      socket.emit('terminal:start', { cols: dims?.cols ?? 80, rows: dims?.rows ?? 24 })
    })

    socket.on('terminal:ready', () => {
      term.writeln('\x1b[32m\u25cf PULSE Terminal\x1b[0m \u2014 your workspace shell\r\n')
    })

    socket.on('terminal:data', (data: string) => {
      term.write(data)

      const clean = data.replace(/\x1B\[[0-9;]*[a-zA-Z]/g, '')

      errorBuf.current += clean
      if (errorBuf.current.length > 5000) errorBuf.current = errorBuf.current.slice(-3000)
      if (ERROR_PATTERNS.some((p) => p.test(clean))) {
        const errorLines = errorBuf.current.split('\n').filter((l) => /error|Error|ERR|failed|Cannot|WebException|BadRequest|Not Found|StatusCode|InvalidOperation/i.test(l)).slice(-5).join('\n')
        if (errorLines.trim()) setLastError(errorLines.trim())
      }

      if (FILE_CHANGE_PATTERNS.some((p) => p.test(clean))) {
        clearTimeout(refreshTimer.current)
        refreshTimer.current = setTimeout(() => {
          window.dispatchEvent(new CustomEvent('pulse:files-changed'))
        }, 1500)
      }
    })

    socket.on('terminal:server-ready', ({ port, url }: { port: number; url: string }) => {
      term.writeln(`\r\n\x1b[32m\u25cf Dev server ready at ${url}\x1b[0m\r\n`)
      addNotification('success', 'Dev Server Ready', `Running on port ${port}`)
    })

    socket.on('terminal:exit', () => {
      term.writeln('\r\n\x1b[31m\u25cf Process exited\x1b[0m')
    })

    socket.on('terminal:error', (err: string) => {
      term.writeln(`\r\n\x1b[31mError: ${err}\x1b[0m`)
    })

    // Error watcher: show "Fix this" when errors detected
    socket.on('terminal:error-detected', (error: any) => {
      term.writeln(`\r\n\x1b[33m⚠ Error detected: ${error.type}\x1b[0m`)
      // Dispatch event so AI panel can show "Fix this" button
      window.dispatchEvent(new CustomEvent('pulse:terminal-error', { detail: error }))
    })

    term.onData((data) => {
      socket.emit('terminal:input', data)
    })

    term.onKey(({ key }) => {
      if (key === '\r') {
        errorBuf.current = ''
        setLastError(null)
        clearTimeout(refreshTimer.current)
        refreshTimer.current = setTimeout(() => {
          window.dispatchEvent(new CustomEvent('pulse:files-changed'))
        }, 2000)
      }
    })

    const handleRunTerminal = (e: Event) => {
      const { commands } = (e as CustomEvent).detail ?? {}
      if (commands && typeof commands === 'string') {
        const psCommands = commands.replace(/\s*&&\s*/g, ' ; ')
        socket.emit('terminal:input', psCommands + '\r')
      }
    }
    window.addEventListener('pulse:run-terminal', handleRunTerminal)

    // Mirror AQ agent terminal output to this terminal
    const handleAQOutput = (e: Event) => {
      const { output } = (e as CustomEvent).detail ?? {}
      if (output) term.write(output.replace(/\n/g, '\r\n'))
    }
    window.addEventListener('pulse:aq-terminal-output', handleAQOutput)

    const resizeObserver = new ResizeObserver(() => {
      fitAddon.fit()
      const dims = fitAddon.proposeDimensions()
      if (dims) socket.emit('terminal:resize', { cols: dims.cols, rows: dims.rows })
    })
    resizeObserver.observe(containerRef.current)

    return () => {
      clearTimeout(refreshTimer.current)
      resizeObserver.disconnect()
      window.removeEventListener('pulse:run-terminal', handleRunTerminal)
      window.removeEventListener('pulse:aq-terminal-output', handleAQOutput)
      socket.disconnect()
      term.dispose()
    }
  }, [])

  const handleFixError = async () => {
    if (!lastError || fixing) return
    setFixing(true)
    try {
      let projectName = ''
      try {
        const memoryStore = JSON.parse(localStorage.getItem('pulse-memory-v2') ?? '{}')
        projectName = memoryStore?.state?.memory?.project ?? ''
      } catch {}

      const res = await authFetch(apiUrl('/api/ai-tools/fix-error'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ error: lastError, projectName, autoFix: true }),
      })
      if (!res.ok || !res.body) throw new Error('Failed')
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let full = '', buf = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.trim().startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.trim().slice(6))
            if (data.text) full += data.text
          } catch {}
        }
      }
      if (full) {
        window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
        const isBackendFix = /server\/|index\.cjs|index\.js|\.env/.test(full)
        if (isBackendFix) {
          const token = getToken()
          fetch(apiUrl('/api/terminal/run'), {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ command: 'Stop-Process -Id (Get-NetTCPConnection -LocalPort 5176 -ErrorAction SilentlyContinue).OwningProcess -Force -ErrorAction SilentlyContinue ; cd task-manager ; node server/index.cjs' }),
          }).catch(() => {})
        }
      }
      setLastError(null)
    } catch {}
    setFixing(false)
  }

  return (
    <div className="terminal-panel">
      <div className="terminal-panel__header">
        <span className="terminal-panel__title">
          <span style={{ color: 'var(--accent-2)' }}>&#9679;</span> Terminal
        </span>
        {lastError && (
          <button onClick={handleFixError} disabled={fixing} style={{
            padding: '2px 8px', borderRadius: 4, border: '1px solid var(--red)',
            background: 'rgba(240,112,112,0.1)', color: 'var(--red)', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            {fixing ? 'Fixing...' : '✨ AI Fix'}
          </button>
        )}
      </div>
      <div ref={containerRef} className="terminal-panel__body" />
    </div>
  )
}
